import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation } from 'lucide-react';
import { 
  Button, 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription
} from '@city-sage/aurum-avis-labs-components';
import { NavigationProvider } from '../../lib/hooks/useNavigation';

interface NavigationProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (provider: NavigationProvider, rememberChoice: boolean) => void;
  destinationName?: string;
}

export const NavigationProviderModal: FC<NavigationProviderModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  destinationName
}) => {
  const { t } = useTranslation();

  const handleProviderSelect = (provider: NavigationProvider) => {
    onSelect(provider, false);
  };

  // Detect if user is on Apple device
  const isAppleDevice = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="rounded-t-3xl border-0 p-0">
        <div className="px-6 py-6">
          <SheetHeader className="text-left mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Navigation className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <SheetTitle className="text-lg font-semibold text-gray-900">
                  Get Directions
                </SheetTitle>
                <SheetDescription className="text-sm text-gray-500 mt-0.5">
                  {destinationName 
                    ? `Navigate to ${destinationName}`
                    : 'Choose your preferred navigation app'
                  }
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          {/* Options */}
          <div className="space-y-3 mb-6">
            {/* Google Maps Option */}
            <button
              className="w-full p-4 flex items-center gap-4 bg-white border-2 border-gray-100 rounded-2xl hover:border-blue-200 hover:bg-blue-50 transition-all duration-200 active:scale-[0.98]"
              onClick={() => handleProviderSelect('google')}
            >
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <div className="w-6 h-6 bg-white rounded-sm flex items-center justify-center">
                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                </div>
              </div>
              <div className="text-left flex-1">
                <div className="font-semibold text-gray-900">Google Maps</div>
                <div className="text-sm text-gray-500 mt-0.5">
                  Works on all devices and browsers
                </div>
              </div>
              <div className="text-blue-600 font-medium text-sm">Open</div>
            </button>

            {/* Apple Maps Option */}
            <button
              className="w-full p-4 flex items-center gap-4 bg-white border-2 border-gray-100 rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 active:scale-[0.98]"
              onClick={() => handleProviderSelect('apple')}
            >
              <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <div className="font-semibold text-gray-900">Apple Maps</div>
                <div className="text-sm text-gray-500 mt-0.5">
                  {isAppleDevice 
                    ? 'Opens in native Apple Maps app'
                    : 'Opens in Apple Maps web version'
                  }
                </div>
              </div>
              <div className="text-gray-600 font-medium text-sm">Open</div>
            </button>
          </div>

          {/* Cancel Button */}
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full py-3 rounded-xl font-medium"
          >
            Cancel
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}; 